import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'dls-inline-flex dls-items-center dls-justify-center dls-whitespace-nowrap dls-rounded-full dls-text-sm dls-font-semibold dls-transition-colors focus-visible:dls-outline-none focus-visible:dls-ring-2 focus-visible:dls-ring-offset-2 focus-visible:dls-ring-neutral disabled:dls-pointer-events-none disabled:dls-opacity-50',
  {
    variants: {
      variant: {
        default: 'dls-bg-neutral dls-text-white dls-shadow-10 hover:dls-bg-neutral-secondary',
        destructive: 'dls-bg-destructive dls-text-destructive-foreground dls-shadow-10 hover:dls-bg-destructive/90',
        secondary: 'dls-bg-neutral-quarternary dls-text-foreground dls-shadow-10 hover:dls-bg-neutral-tertiary',
        ghost: 'hover:dls-bg-neutral-quarternary hover:dls-text-foreground',
        link: 'dls-text-primary dls-underline-offset-4 hover:dls-underline',
      },
      size: {
        default: 'dls-h-9 dls-px-4 dls-py-2',
        sm: 'dls-h-8 dls-px-3 dls-text-xs',
        lg: 'dls-h-10 dls-px-8',
        icon: 'dls-h-9 dls-w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
